// File: frontend/src/components/common/ShareMenu.tsx

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaShareAlt,
  FaLink,
  FaWhatsapp,
  FaTwitter,
  FaFacebookF,
  FaTelegramPlane,
  FaMobileAlt,
} from "react-icons/fa";
import {
  toAbsoluteUrl,
  copyTextToClipboard,
  openShareWindow,
  buildWhatsappShareUrl,
  buildTwitterShareUrl,
  buildFacebookShareUrl,
  buildTelegramShareUrl,
} from "../../utils/shareUtils";
import "./ShareMenu.css";

export interface ShareMenuProps {
  /** Root-relative path or absolute URL to share */
  url: string;
  /** Text used as the share message (topic title, or entry author + topic) */
  title: string;
  /**
   * Visual variant:
   *  - "toolbar": bordered pill button, matches the entry toolbar buttons
   *  - "compact": borderless icon button, used inside the entry metadata row
   */
  variant?: "toolbar" | "compact";
  /** Extra class applied to the wrapper for page-specific positioning */
  className?: string;
  /**
   * Optional extra menu item rendered at the bottom of the list —
   * used by EntryRow for the "entry no kopyala" action.
   */
  extraItems?: { key: string; label: string; icon: React.ReactNode; onClick: () => void }[];
}

/**
 * Reusable share dropdown ("paylaş").
 * Used both for topic pages (entry toolbar) and for single entries (EntryRow),
 * so the available targets stay identical everywhere in the app.
 */
export const ShareMenu: React.FC<ShareMenuProps> = ({
  url,
  title,
  variant = "toolbar",
  className,
  extraItems,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close the dropdown when clicking anywhere outside of it
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Clear the pending "copied" timer on unmount so no state update leaks
  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const absoluteUrl = toAbsoluteUrl(url);

  const handleCopyLink = useCallback(async () => {
    const ok = await copyTextToClipboard(absoluteUrl);
    if (ok) {
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => {
        setCopied(false);
        setOpen(false);
      }, 1200);
    }
  }, [absoluteUrl]);

  // Native OS share sheet — only offered when the browser supports it (mobile)
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const handleNativeShare = useCallback(async () => {
    try {
      await navigator.share({ title, url: absoluteUrl });
      setOpen(false);
    } catch {
      // User dismissed the native sheet — nothing to do
    }
  }, [title, absoluteUrl]);

  const handleTarget = useCallback((href: string) => {
    openShareWindow(href);
    setOpen(false);
  }, []);

  return (
    <div className={`share-menu share-menu--${variant}${className ? ` ${className}` : ""}`} ref={wrapperRef}>
      <button
        type="button"
        className="share-menu__trigger"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        title={t("share_menu_title", "paylaş")}
        aria-label={t("share_menu_title", "paylaş")}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <FaShareAlt />
        {variant === "toolbar" && <span className="share-menu__trigger-label">{t("share_menu_title", "paylaş")}</span>}
      </button>

      {open && (
        <div className="share-menu__dropdown" role="menu">
          <button type="button" className="share-menu__item" onClick={handleCopyLink}>
            <FaLink className="share-menu__icon" />
            {copied ? t("share_menu_copied", "kopyalandı") : t("share_menu_copy_link", "link'i kopyala")}
          </button>

          <button
            type="button"
            className="share-menu__item"
            onClick={() => handleTarget(buildWhatsappShareUrl(absoluteUrl, title))}
          >
            <FaWhatsapp className="share-menu__icon" />
            {t("share_menu_whatsapp", "whatsapp'ta paylaş")}
          </button>

          <button
            type="button"
            className="share-menu__item"
            onClick={() => handleTarget(buildTwitterShareUrl(absoluteUrl, title))}
          >
            <FaTwitter className="share-menu__icon" />
            {t("share_menu_twitter", "x'te paylaş")}
          </button>

          <button
            type="button"
            className="share-menu__item"
            onClick={() => handleTarget(buildFacebookShareUrl(absoluteUrl))}
          >
            <FaFacebookF className="share-menu__icon" />
            {t("share_menu_facebook", "facebook'ta paylaş")}
          </button>

          <button
            type="button"
            className="share-menu__item"
            onClick={() => handleTarget(buildTelegramShareUrl(absoluteUrl, title))}
          >
            <FaTelegramPlane className="share-menu__icon" />
            {t("share_menu_telegram", "telegram'da paylaş")}
          </button>

          {/* Extra caller-provided actions, e.g. "entry no kopyala" */}
          {extraItems?.map((item) => (
            <button
              key={item.key}
              type="button"
              className="share-menu__item"
              onClick={() => { item.onClick(); setOpen(false); }}
            >
              <span className="share-menu__icon">{item.icon}</span>
              {item.label}
            </button>
          ))}

          {canNativeShare && (
            <button type="button" className="share-menu__item" onClick={handleNativeShare}>
              <FaMobileAlt className="share-menu__icon" />
              {t("share_menu_device", "cihazda paylaş")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};